<script setup lang="ts">
import Footer from "@/components/Footer.vue";
import {ref} from "vue";

const day = ref(0),
    month = ref(0),
    year = ref(0),
    rememberMe = ref(false),
    errorMessage = ref(''),
    emit = defineEmits(['ageVerified']);

const currentYear = new Date().getFullYear();
const days = Array.from({ length: 31 }, (_, index) => index + 1);
const months = Array.from({ length: 12 }, (_, index) => index + 1);
const years = Array.from({ length: currentYear - 1921 + 1 }, (_, index) => currentYear - index);

const getDisplayChars = (value: number, length: number, placeholder: string) => {
    const displayValue = value === 0 ? placeholder.repeat(length) : String(value).padStart(length, '0');
    return displayValue.split('');
};

if (localStorage.getItem('user_age'))
    emit('ageVerified', parseInt(localStorage.getItem('user_age')!));

const verifyAge = () => {
    if (day.value === 0 || month.value === 0 || year.value === 0) {
        errorMessage.value = 'Podaj prawidłową datę';
        return;
    }

    const age = calculateAge(
        year.value,
        month.value,
        day.value);

    if (age < 18) {
        errorMessage.value = 'Strona przeznaczona tylko dla osób 18+';
    } else {
        errorMessage.value = '';
        if (rememberMe.value) {
            localStorage.setItem('user_age', age.toString());
        }
        emit('ageVerified', age);
    }
}

const calculateAge = (y: number, m: number, d: number) => {
  const birthday = new Date(y, m - 1, d);
    const ageDifMs = Date.now() - birthday.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
}
</script>

<template>
    <div id="age_verification" class="w-full text-primary">
        <div class="container mx-auto text-center p-4 py-8">
            <h1 class="age-title text-[1.75rem] md:text-[2.5rem] lg:text-[3.125rem] text-white uppercase font-medium tracking-[20%] mt-8 pb-4 lg:pb-8">Podaj swoją datę urodzenia</h1>
            <div class="w-28 lg:w-52 h-px bg-default-white/70 mx-auto mb-14"></div>

            <div class="py-14 px-7 flex flex-col gap-10 max-w-4xl m-auto">
                <div class="error-label text-sm text-default-red" v-show="errorMessage">{{ errorMessage}}</div>
                <div class="age-selects text-primary flex flex-col gap-6 lg:gap-10">
                    <div class="flex flex-row gap-4 lg:gap-6 justify-center">
                        <div class="age-select-group" aria-label="Dzień">
                            <select id="day" v-model.number="day" name="day" class="age-select-native" required>
                                <option :value="0">DD</option>
                                <option v-for="dayOption in days" :key="dayOption" :value="dayOption">
                                    {{ dayOption }}
                                </option>
                            </select>
                            <div class="age-select-visual">
                                <div
                                    v-for="(char, index) in getDisplayChars(day, 2, 'D')"
                                    :key="`day-${index}`"
                                    class="age-select-slot"
                                    :style="{ '--slot-delay': `${0.35 + index * 0.07}s` }"
                                >
                                    <span class="age-select-char">{{ char }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="age-select-group" aria-label="Miesiąc">
                            <select id="month" v-model.number="month" name="month" class="age-select-native" required>
                                <option :value="0">MM</option>
                                <option v-for="monthOption in months" :key="monthOption" :value="monthOption">
                                    {{ monthOption }}
                                </option>
                            </select>
                            <div class="age-select-visual">
                                <div
                                    v-for="(char, index) in getDisplayChars(month, 2, 'M')"
                                    :key="`month-${index}`"
                                    class="age-select-slot"
                                    :style="{ '--slot-delay': `${0.49 + index * 0.07}s` }"
                                >
                                    <span class="age-select-char">{{ char }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mx-auto">
                        <div class="age-select-group age-select-group-year" aria-label="Rok">
                            <select id="year" v-model.number="year" name="year" class="age-select-native" required>
                                <option :value="0">RRRR</option>
                                <option v-for="yearOption in years" :key="yearOption" :value="yearOption">
                                    {{ yearOption }}
                                </option>
                            </select>
                            <div class="age-select-visual">
                                <div
                                    v-for="(char, index) in getDisplayChars(year, 4, 'R')"
                                    :key="`year-${index}`"
                                    class="age-select-slot"
                                    :style="{ '--slot-delay': `${0.63 + index * 0.07}s` }"
                                >
                                    <span class="age-select-char">{{ char }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="age-checkbox flex gap-4 mx-auto items-center">
                    <input type="checkbox"
                           id="remember_me"
                           v-model="rememberMe"
                           class="w-4 h-4 shrink-0"
                    />
                    <label for="remember_me" class="text-xl lg:text-2xl text-white">Zapamiętaj mój wiek</label>
                </div>
            </div>
            <button @click.prevent="verifyAge" class="age-button block !text-4xl !mt-7">WEJDŹ NA STRONĘ</button>
            <div class="lg:w-1/2 m-auto pt-16 lg:pt-24">

            </div>
        </div>
    </div>
    <div class="bg-default-white">
        <div class="lg:w-1/2 m-auto">
            <Footer/>
        </div>
    </div>
</template>

<style scoped>
.age-title {
    opacity: 0;
    transform: translateY(-18px);
    animation: age-title-in 0.7s ease 0.2s forwards;
}

#age_verification {
    background-image:
        url("../assets/images/backgrounds/age/peopleMobile.webp"),
        url("../assets/images/backgrounds/age/bgSkyMobile.webp");
    background-size:
        contain,
        cover;
    background-position:
        bottom center,
        bottom center;
    background-repeat:
        no-repeat,
        no-repeat;
}

@media only screen and (min-width: 768px) {
    #age_verification {
        background-image:
            url("../assets/images/backgrounds/age/people.webp"),
            url("../assets/images/backgrounds/age/bgSky.webp");
    }
}

input {
    background: var(--color-default-white);
    font-size: 2rem;
    padding-inline: 1rem;
}

input::placeholder {
    color: #000;
    text-align: center;
}

input[type=checkbox] {
    padding: 0.15rem;
}

input[type="checkbox"]::before {
    background-color: var(--color-primary);
}

button {
    background: var(--color-default-red);
    font-weight: 400;
    padding: 1rem 2rem;
}

.age-button {
    position: relative;
    overflow: hidden;
    transform: translateY(14px);
    opacity: 0;
    animation: age-button-in 0.5s ease 1.15s forwards;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.age-button::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 -140%;
    width: 42%;
    background: linear-gradient(115deg, transparent 0%, rgba(255, 255, 255, 0.16) 35%, rgba(255, 255, 255, 0.45) 50%, transparent 100%);
    transform: skewX(-22deg);
    transition: left 0.45s ease;
    pointer-events: none;
}

.age-button:hover {
    transform: scale(1.03);
    box-shadow: 0 14px 34px rgba(237, 27, 35, 0.28);
}

.age-button:hover::before {
    left: 135%;
}

select {
    background: var(--color-default-white);
    text-align: -webkit-center;
    text-align-last: center;
    margin: auto;
    width: 100%;
    font-size: 2.125rem;
    max-width: 50rem;
}

.age-select-group {
    position: relative;
}

.age-select-visual {
    display: flex;
    gap: 0.5rem;
    pointer-events: none;
}

.age-select-slot {
    width: 3.5rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-default-white);
    box-sizing: border-box;
    transform: scale(0.85);
    opacity: 0;
    animation: age-slot-in 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) var(--slot-delay) forwards;
}

.age-select-char {
    color: var(--color-primary);
    font-size: 2rem;
    line-height: 1;
    text-transform: uppercase;
}

.age-select-native {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
}

.age-checkbox {
    opacity: 0;
    transform: translateY(10px);
    animation: age-checkbox-in 0.5s ease 1s forwards;
}

@keyframes age-title-in {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes age-slot-in {
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes age-checkbox-in {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes age-button-in {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media screen and (min-width: 1024px) {
    .age-select-slot {
        width: 5rem;
        height: 5.75rem;
    }

    .age-select-char {
        font-size: 3rem;
    }

    select {
        font-size: 3.125rem;
    }
}
</style>
